import React, { useId } from "react";
import ModalDialog from "./ModalDialog";
import TextInput from "./TextInput";
import SelectInput from "./SelectInput";
import { BeatLoader } from "react-spinners";
import { useTranslation } from "react-i18next";
import tailwindClassnames from "./TailwindClassnames";
import RolePermissionsInfo from "./RolePermissionsInfo";
import { RoleScope } from "./rolePermissions";

interface InviteUsersModalProps {
  isOpen: boolean;
  roles: any[];
  /**
   * True while the caller is still fetching `roles`. The Role select then
   * shows react-select's loading state ("Loading...") instead of treating the
   * not-yet-arrived list as empty and reading "No options" — the symptom
   * AKL-848 was filed about. Optional and off by default so a caller that has
   * its roles in hand renders exactly as before.
   */
  rolesLoading?: boolean;
  /**
   * Which role set is on offer. Shared verbatim by the organization and
   * project users tabs, and neither the props nor the role ids can tell them
   * apart — role ids 1-4 are identical across both enums — so each caller
   * states it.
   */
  scope: RoleScope;
  inviteData: any;
  onClose: () => void;
  onChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => void;
  onSelectUsersRole: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  onSendInvite: (e: React.FormEvent) => void;
  sendingInvite: boolean;
}

const InviteUsersModal: React.FC<InviteUsersModalProps> = ({
  roles,
  rolesLoading = false,
  scope,
  inviteData,
  isOpen,
  onClose,
  onChange,
  onSelectUsersRole,
  onSendInvite,
  sendingInvite,
}) => {
  const { t } = useTranslation();
  const titleId = useId();

  if (!isOpen) return null;
  let isEmailValid = inviteData.email !== "" && inviteData.email.includes("@");
  let isRoleSelected = inviteData.role_id !== 0;

  return (
    <ModalDialog
      onClose={onClose}
      busy={sendingInvite}
      labelledBy={titleId}
      overlayClassName="z-20"
      panelClassName="max-w-md"
    >
      <>
        <div className={tailwindClassnames.modalHeaderRow}>
          <div className="flex items-center gap-3">
            <h2 id={titleId} className={tailwindClassnames.modalTitle}>
              {t("Invite User")}
            </h2>
          </div>
          <button
            type="button"
            className={tailwindClassnames.modalCloseButton}
            onClick={onClose}
            aria-label={t("Close")}
          >
            &times;
          </button>
        </div>
        <div className={`${tailwindClassnames.modalBody} space-y-4`}>
          <TextInput
            name="email"
            label={t("Email")}
            placeholder=""
            value={inviteData.email || ""}
            disabled={false}
            className={tailwindClassnames.detailsPageTextInput}
            type="text"
            onChange={onChange}
          />

          <SelectInput
            options={roles.map((role) => ({
              value: role.value,
              label: t(role.name),
            }))}
            className={"w-48 mr-1 w-full"}
            defaultValue={inviteData.role_id}
            isDisabled={false}
            isLoading={rolesLoading}
            isClearable={true}
            isSearchable={true}
            isMulti={false}
            errors={""}
            name={t("Role")}
            noOptionsMessage={() => t("No options available")}
            labelSuffix={<RolePermissionsInfo scope={scope} />}
            onChange={onSelectUsersRole}
            searchBar={false}
          />
        </div>
        <div className={tailwindClassnames.modalFooter}>
          <button
            type="button"
            onClick={onClose}
            className={tailwindClassnames.btnSecondary}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            onClick={onSendInvite}
            disabled={!isEmailValid || !isRoleSelected || sendingInvite}
            className={`relative ${tailwindClassnames.btnPrimary}`}
          >
            {/* Label stays in layout while sending so the button keeps its
                width; currentColor spinner reads on the filled button in both
                themes. */}
            <span className={sendingInvite ? "invisible" : ""}>
              {t("Send")}
            </span>
            {sendingInvite && (
              <span className="absolute inset-0 flex items-center justify-center">
                <BeatLoader
                  color="currentColor"
                  loading={true}
                  size={10}
                  margin={5}
                />
              </span>
            )}
          </button>
        </div>
      </>
    </ModalDialog>
  );
};

export default InviteUsersModal;
